import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FaCheckCircle, FaTimes } from 'react-icons/fa';
import { CustomButton10Co, CustomButton10Ca, RadioButton1 } from './Templates';
import { supabase } from './data/supabaseClient';
import { useToast } from './hooks/ToastContext';
import Spinner from './common/Spinner';
import './CompletionNotificationPopup.css';

/**
 * CompletionNotificationPopup - Shows success message when a ride/package is completed
 * and offers the option to create a similar post for another date
 */
const CompletionNotificationPopup = ({ 
  isOpen, 
  onClose, 
  postType, // 'ride' or 'package'
  postId,
  postData,
  onRenew 
}) => {
  const { t } = useTranslation('common');
  const { addToast } = useToast();
  const [postAction, setPostAction] = useState('archive');
  const [isUpdating, setIsUpdating] = useState(false);
  
  // Smart default based on travel_type
  useEffect(() => {
    if (postData?.travel_type === 'Daily' || postData?.travel_type === 'daily') {
      setPostAction('keep');
    } else {
      // For One-time posts or if travel date has passed
      const travelDate = postData?.travel_date ? new Date(postData.travel_date) : null;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      if (travelDate && travelDate < today) {
        setPostAction('archive'); // Past date, suggest archiving
      } else {
        setPostAction('archive'); // Default for one-time
      }
    }
  }, [postData]);

  if (!isOpen) return null;

  const handleArchivePost = async () => {
    try {
      const table = postType === 'ride' ? 'posts' : 'packages';
      const { error } = await supabase
        .from(table)
        .update({ 
          status: 'completed',
          completed_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', postId);
        
      if (error) throw error;
      
      return true;
    } catch (error) {
      console.error('Error archiving post:', error);
      addToast({
        type: 'error',
        title: t('errors.archive_failed', { defaultValue: 'Archive Failed' }),
        description: error.message
      });
      return false;
    }
  };

  const handleRenew = async () => {
    setIsUpdating(true);
    
    // Apply the selected action first
    if (postAction === 'archive') {
      const success = await handleArchivePost();
      if (!success) {
        setIsUpdating(false);
        return;
      }
    }
    // If 'keep', do nothing - post remains active
    
    // Then open renewal form
    if (onRenew) {
      onRenew(postId, postData);
    }
    
    setIsUpdating(false);
    onClose();
  };

  const handleClose = async () => {
    setIsUpdating(true);
    
    // Apply the selected action
    if (postAction === 'archive') {
      await handleArchivePost();
    }
    // If 'keep', do nothing - post remains active
    
    setIsUpdating(false);
    onClose();
  };

  return (
    <div className="CNP-overlay" onClick={handleClose}>
      <div className="CNP-modal" onClick={(e) => e.stopPropagation()}>
        <button className="CNP-close-button" onClick={handleClose} aria-label={t('buttons.close') || 'Close'}>
          <FaTimes />
        </button>
        
        <div className="CNP-content">
          <div className="CNP-success-icon">
            <FaCheckCircle size={60} color="#4CAF50" />
          </div>
          
          <h2 className="CNP-title">
            {t('completion.success_title', { defaultValue: '🎉 Success!' })}
          </h2>
          
          <p className="CNP-message">
            {postType === 'ride' 
              ? t('completion.ride_completed', { 
                  defaultValue: 'Your ride has been completed successfully!' 
                })
              : t('completion.package_delivered', { 
                  defaultValue: 'Your package has been delivered successfully!' 
                })
            }
          </p>
          
          {/* Post Action Toggle Section */}
          <div className="CNP-post-action-section">
            <p className="CNP-action-label">
              {t('completion.what_to_do_with_post', { 
                defaultValue: 'What to do with this post?' 
              })}
            </p>
            
            <div className="CNP-toggle-container">
              <RadioButton1
                name="postAction"
                label={
                  <div className="CNP-option-content">
                    <span className="CNP-option-title">
                      {t('completion.keep_active', { defaultValue: 'Keep Post Active' })}
                    </span>
                    <span className="CNP-option-desc">
                      {t('completion.keep_active_desc', { 
                        defaultValue: postData?.travel_type === 'Daily' 
                          ? 'Post remains visible for future daily rides'
                          : 'Post remains visible for future rides' 
                      })}
                    </span>
                  </div>
                }
                value="keep"
                checked={postAction === 'keep'}
                onChange={(e) => setPostAction(e.target.value)}
              />
              
              <RadioButton1
                name="postAction"
                label={
                  <div className="CNP-option-content">
                    <span className="CNP-option-title">
                      {t('completion.archive_post', { defaultValue: 'Archive Post' })}
                    </span>
                    <span className="CNP-option-desc">
                      {t('completion.archive_post_desc', { 
                        defaultValue: 'Remove from active listings' 
                      })}
                    </span>
                  </div>
                }
                value="archive"
                checked={postAction === 'archive'}
                onChange={(e) => setPostAction(e.target.value)}
              />
            </div>
          </div>
          
          <p className="CNP-question">
            {postType === 'ride'
              ? t('completion.post_similar_ride', { 
                  defaultValue: 'Would you like to post a similar ride for another date?' 
                })
              : t('completion.post_similar_package', { 
                  defaultValue: 'Would you like to post a similar package for another date?' 
                })
            }
          </p>
          
          <div className="CNP-actions">
            <CustomButton10Co
              onClick={handleRenew}
              disabled={isUpdating}
              style={{ minWidth: '150px' }}
            >
              {isUpdating ? (
                <Spinner size="small" color="white" />
              ) : (
                t('completion.post_again', { defaultValue: 'Post Again' })
              )}
            </CustomButton10Co>
            <CustomButton10Ca
              onClick={handleClose}
              disabled={isUpdating}
              label={isUpdating ? '...' : t('completion.close', { defaultValue: 'Finish' })}
              style={{ minWidth: '150px' }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompletionNotificationPopup;